/**
 * "See it on the page" (WS-22): the draft's pages as the client gets them —
 * pictures the server made from the draft — opened at an issue's page, every
 * open issue outlined where it sits, the one asked about solid. A click on
 * an outline closes the pages and selects that issue's card.
 */

import { useEffect, useRef, useState } from 'react';

import { Center, Loader, Modal, Stack, Text } from '@mantine/core';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { DraftPages, ReviewIssue } from '@/types/workbench';
import { toError } from '@/utils/errors';
import classes from './DraftView.module.css';

interface PagesModalProps {
  reportId: string;
  draftId: string;
  /** The page to open at. */
  page: number;
  /** The issue asked about: outlined solid. */
  issueId: string | null;
  /** The draft's open issues, for an outline's title. */
  issues: ReviewIssue[];
  onClose: () => void;
  onPickIssue: (issueId: string) => void;
}

export function PagesModal({
  reportId,
  draftId,
  page,
  issueId,
  issues,
  onClose,
  onPickIssue,
}: Readonly<PagesModalProps>) {
  const [pages, setPages] = useState<DraftPages | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const frames = useRef(new Map<number, HTMLDivElement>());

  useEffect(() => {
    let cancelled = false;
    setPages(null);
    setFailed(null);
    vnextApiClient
      .getDraftPages(reportId, draftId)
      .then((read) => {
        if (!cancelled) {
          setPages(read);
        }
      })
      .catch((err: unknown) => {
        Sentry.captureException(toError(err));
        if (!cancelled) {
          setFailed(toError(err, "The pages couldn't be made").message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reportId, draftId]);

  // Opened at the page asked about.
  useEffect(() => {
    if (pages) {
      frames.current.get(page)?.scrollIntoView?.({ block: 'start' });
    }
  }, [pages, page]);

  const messages = new Map(issues.map((i) => [i.id, i.message]));
  return (
    <Modal
      opened
      onClose={onClose}
      size="xl"
      title={pages ? `As the client gets it · ${pages.pageCount} pages` : 'As the client gets it'}
      data-testid="pages-modal"
    >
      {failed ? (
        <Text size="sm" c="red">
          {failed}
        </Text>
      ) : !pages ? (
        <Center h={160}>
          <Stack align="center" gap="xs">
            <Loader size="sm" />
            <Text size="xs" c="dimmed">
              Making the pages (the first time takes a little while)…
            </Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap={0}>
          {pages.pages.map((p) => (
            <div
              key={p.number}
              ref={(el) => {
                if (el) {
                  frames.current.set(p.number, el);
                } else {
                  frames.current.delete(p.number);
                }
              }}
              className={classes.pageFrame}
              style={{ maxWidth: p.width }}
              data-testid={`page-${p.number}`}
            >
              <img src={p.url} alt={`Page ${p.number}`} width={p.width} height={p.height} />
              {pages.faults
                .filter((f) => f.page === p.number)
                .map((f) => (
                  <button
                    key={f.reviewIssueId}
                    type="button"
                    className={`${classes.fault} ${f.box ? '' : classes.faultPage}`}
                    style={
                      f.box
                        ? {
                            left: `${f.box[0] * 100}%`,
                            top: `${f.box[1] * 100}%`,
                            width: `${(f.box[2] - f.box[0]) * 100}%`,
                            height: `${(f.box[3] - f.box[1]) * 100}%`,
                          }
                        : undefined
                    }
                    data-current={f.reviewIssueId === issueId}
                    title={messages.get(f.reviewIssueId) ?? 'An issue'}
                    aria-label={messages.get(f.reviewIssueId) ?? 'An issue'}
                    onClick={() => onPickIssue(f.reviewIssueId)}
                    data-testid={`fault-${f.reviewIssueId}`}
                  />
                ))}
            </div>
          ))}
        </Stack>
      )}
    </Modal>
  );
}
